"use client";

import React from "react";
import { useRouter, usePathname } from "next/navigation";
import { ContentCard } from "fansunited-sports-ui";
import { CardPagination } from "@/components/ui/CardPagination";
import { BrandedHeader } from "@/components/branded";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { AdSlot } from "@/features/ads/AdSlot";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { Post, Category } from "@/payload-types";
import { getMediaFieldUrl } from "@/utilities/media/getMediaUrl";
import type { LocaleCode } from "@/lib/labels/types";

interface VideosLayoutWithSidebarProps {
    posts: Post[];
    currentPage: number;
    totalPages: number;
    locale: LocaleCode;
    categories: Category[];
    selectedCategoryId: number | null;
    headerColors?: ResolvedHeaderColors;
    breadcrumbs?: React.ReactNode;
}

export function VideosLayoutWithSidebar({
    posts,
    currentPage,
    totalPages,
    locale,
    categories,
    selectedCategoryId,
    headerColors,
    breadcrumbs,
}: VideosLayoutWithSidebarProps) {
    const labels = useLabels();
    const router = useRouter();
    const pathname = usePathname();

    const handlePageChange = (page: number) => {
        // Build URL preserving category filter
        const params = new URLSearchParams();
        if (selectedCategoryId) {
            params.set("category", String(selectedCategoryId));
        }
        if (page > 1) {
            params.set("page", String(page));
        }
        const url = params.toString() ? `${pathname}?${params.toString()}` : pathname;
        router.push(url);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCategoryChange = (categoryId: number | null) => {
        // Reset to page 1 when changing category
        const params = new URLSearchParams();
        if (categoryId) {
            params.set("category", String(categoryId));
        }
        const url = params.toString() ? `${pathname}?${params.toString()}` : pathname;
        router.push(url);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    const getImageUrl = (heroImage: Post["heroImage"]): string | undefined => {
        return getMediaFieldUrl(heroImage, "medium") ?? undefined;
    };

    const headerTitle = (labels as any).nav?.videos ?? "Videos";

    return (
        <div className="min-h-screen">
            <BrandedHeader
                title={headerTitle}
                subtitle=""
                colors={headerColors?.colors}
                backgroundImageUrl={headerColors?.backgroundImageUrl}
            />

            {breadcrumbs}

            <div className="max-w-350 mx-auto px-4 py-8 space-y-8">
                <AdSlot id="content-top" />

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main Content - Vertical Video List */}
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col gap-4">
                            {posts.map((post) => (
                                <ContentCard
                                    key={post.id}
                                    content={{
                                        id: String(post.id),
                                        type: "article",
                                        link: `/videos/${post.slug}`,
                                        title: post.title,
                                        description: post.summary || "",
                                        image: {
                                            url: getImageUrl(post.heroImage) || "",
                                            alt: post.title,
                                        },
                                        metadata: {
                                            time: formatDate(post.publishedAt),
                                        },
                                    }}
                                    variant="standardHorizontal"
                                    styles={{
                                        typography: {
                                            title: "text-lg font-semibold",
                                            description: "text-sm text-muted-foreground line-clamp-2",
                                        },
                                    }}
                                />
                            ))}
                        </div>

                        {/* Empty State */}
                        {posts.length === 0 && (
                            <div className="text-center py-12 text-muted-foreground">No videos found.</div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-8 flex justify-center">
                                <CardPagination
                                    variant="numbered"
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <aside className="w-full lg:w-80 shrink-0">
                        <div className="sticky top-below-nav-gap space-y-6">
                            {/* Category Filter */}
                            {categories.length > 0 && (
                                <div className="bg-card border border-border rounded-lg p-4">
                                    <h3 className="font-semibold text-foreground mb-3">Categories</h3>
                                    <div className="flex flex-col gap-2">
                                        <button
                                            onClick={() => handleCategoryChange(null)}
                                            className={`px-3 py-2 rounded-md text-sm font-medium text-left transition-colors ${
                                                selectedCategoryId === null
                                                    ? "bg-primary text-primary-foreground"
                                                    : "bg-muted hover:bg-muted/80 text-foreground"
                                            }`}
                                        >
                                            All
                                        </button>
                                        {categories.map((category) => (
                                            <button
                                                key={category.id}
                                                onClick={() => handleCategoryChange(category.id)}
                                                className={`px-3 py-2 rounded-md text-sm font-medium text-left transition-colors ${
                                                    selectedCategoryId === category.id
                                                        ? "bg-primary text-primary-foreground"
                                                        : "bg-muted hover:bg-muted/80 text-foreground"
                                                }`}
                                            >
                                                {category.title}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <AdSlot id="sidebar-top" />

                            <AdSlot id="sidebar-bottom" />
                        </div>
                    </aside>
                </div>

                <AdSlot id="content-bottom" />
            </div>
        </div>
    );
}
